/**
 * 커미션 관리 페이지
 * 
 * 접근 권한: can_access_commission = true 인 사용자
 * 
 * 기능:
 * - 월별 커미션 조회
 * - 커미션 추가/수정/삭제
 * - 총 커미션 합계 표시
 * - CSV 미리보기 및 다운로드
 * - Facture PDF 내보내기 (특수 권한 사용자 전용)
 */
import React, { useState, useEffect, useContext, useMemo, useCallback } from 'react';
import { DateTime } from 'luxon';
import { UserContext } from '../contexts/UserContext';
import { canDownloadFacture, hasCommissionAccess } from '../utils/permissionConfig';
import { downloadInvoicePdf, getOptionalInvoiceReference } from '../services/accountingDocuments';
import { loadAccountingInvoiceReferences } from '../services/accountingInvoiceReferenceApi';
import { getThemeForPage } from '../styles/themes';
import styles from '../styles/CommissionStyles.module.css';
import commonStyles from '../styles/CommonStyles.module.css';
import * as commissionApi from '../services/commissionApi';
import ScrollPicker from '../components/ScrollPicker';
import CsvPreviewModal from '../components/CsvPreviewModal';

// 다국어 텍스트
const TEXT = {
    title: { ko: '커미션 관리', en: 'Commission Management', fr: 'Gestion des Commissions' },
    totalCommission: { ko: '이번 달 총 커미션', en: 'Total Commission This Month', fr: 'Commission Totale du Mois' },
    add: { ko: '추가', en: 'Add', fr: 'Ajouter' },
    edit: { ko: '수정', en: 'Edit', fr: 'Modifier' },
    delete: { ko: '삭제', en: 'Delete', fr: 'Supprimer' },
    save: { ko: '저장', en: 'Save', fr: 'Enregistrer' },
    cancel: { ko: '취소', en: 'Cancel', fr: 'Annuler' },
    artistName: { ko: '타투이스트 이름', en: 'Artist Name', fr: 'Nom de l\'Artiste' },
    position: { ko: '지위', en: 'Position', fr: 'Statut' },
    amount: { ko: '금액', en: 'Amount', fr: 'Montant' },
    receivedDate: { ko: '받은 날짜', en: 'Received Date', fr: 'Date de Réception' },
    platform: { ko: '플랫폼', en: 'Platform', fr: 'Plateforme' },
    memo: { ko: '메모', en: 'Memo', fr: 'Note' },
    noData: { ko: '데이터가 없습니다', en: 'No data', fr: 'Aucune donnée' },
    confirmDelete: { ko: '정말 삭제하시겠습니까?', en: 'Are you sure you want to delete?', fr: 'Êtes-vous sûr de vouloir supprimer?' },
    addCommission: { ko: '커미션 추가', en: 'Add Commission', fr: 'Ajouter Commission' },
    editCommission: { ko: '커미션 수정', en: 'Edit Commission', fr: 'Modifier Commission' },
    noAccess: { ko: '접근 권한이 없습니다', en: 'No access permission', fr: 'Aucune autorisation d\'accès' },
    loadError: { ko: '데이터를 불러오는데 실패했습니다', en: 'Failed to load data', fr: 'Échec du chargement des données' },
    saveError: { ko: '저장에 실패했습니다', en: 'Failed to save', fr: 'Échec de l\'enregistrement' },
    deleteError: { ko: '삭제에 실패했습니다', en: 'Failed to delete', fr: 'Échec de la suppression' },
    // Position 옵션
    resident: { ko: '레지던트', en: 'Resident', fr: 'Résident' },
    guest: { ko: '게스트', en: 'Guest', fr: 'Guest' },
    staff: { ko: '스태프', en: 'Staff', fr: 'Staff' },
    other: { ko: '기타', en: 'Other', fr: 'Autre' },
    // Platform 옵션
    cash: { ko: '현금', en: 'Cash', fr: 'Espèces' },
    paypal: { ko: '페이팔', en: 'PayPal', fr: 'PayPal' },
    transfer: { ko: '계좌이체', en: 'Bank Transfer', fr: 'Virement' },
};

const t = (key, lang) => TEXT[key]?.[lang] || TEXT[key]?.['ko'] || key;

// Position 옵션
const POSITION_OPTIONS = ['resident', 'guest', 'staff', 'other'];

// Platform 옵션
const PLATFORM_OPTIONS = ['cash', 'paypal', 'transfer', 'other'];

// 월 이름 - 현재 미사용
// eslint-disable-next-line no-unused-vars
const MONTH_NAMES = {
    ko: ['1월', '2월', '3월', '4월', '5월', '6월', '7월', '8월', '9월', '10월', '11월', '12월'],
    en: ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'],
    fr: ['Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin', 'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'],
};

export default function CommissionManagementPage() {
    const { userInfo } = useContext(UserContext);
    const lang = userInfo?.preferred_language || 'ko';
    const currentTheme = getThemeForPage('/commission-management');

    // 접근 권한 체크 (백엔드에서 can_access_commission 컬럼으로 관리)
    const hasAccess = hasCommissionAccess(userInfo);
    const canExportFacture = canDownloadFacture(userInfo);

    // PWA 노치 색상을 페이지 배경색(#f8f8f8)과 통일
    useEffect(() => {
        const themeColorMeta = document.querySelector('meta[name="theme-color"]');
        if (themeColorMeta) {
            themeColorMeta.setAttribute('content', '#fffdf8');
        }
    }, []);

    // 현재 선택된 년/월
    const now = new Date();
    const [selectedYear, setSelectedYear] = useState(now.getFullYear());
    const [selectedMonth, setSelectedMonth] = useState(now.getMonth() + 1);

    // 커미션 데이터
    const [commissions, setCommissions] = useState([]);
    const [totalAmount, setTotalAmount] = useState(0);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);

    // 모달 상태
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingCommission, setEditingCommission] = useState(null);
    const [isSaving, setIsSaving] = useState(false);
    const [formData, setFormData] = useState({
        artist_name: '',
        position: 'resident',
        amount: '',
        received_date: new Date().toISOString().split('T')[0],
        platform: 'cash',
        memo: '',
    });

    // 년도 옵션 (현재 년도 기준 ±5년)
    const currentYear = now.getFullYear();
    const years = Array.from({ length: 11 }, (_, i) => currentYear - 5 + i);
    const months = Array.from({ length: 12 }, (_, i) => i + 1);

    // 연도별/월별 합계 캐시
    const [yearlyTotals, setYearlyTotals] = useState({});
    const [monthlyTotals, setMonthlyTotals] = useState({});

    // CSV 미리보기 상태
    const [csvPreviewOpen, setCsvPreviewOpen] = useState(false);
    const [csvPreviewData, setCsvPreviewData] = useState([]);

    // 금액 포맷 (1000 -> 1k, 1000000 -> 1M)
    const formatRevenue = (amount) => {
        if (!amount || amount === 0) return '-';
        if (amount >= 1000000) return `${(amount / 1000000).toFixed(1)}M€`;
        if (amount >= 1000) return `${(amount / 1000).toFixed(1)}k€`;
        return `${amount}€`;
    };

    // ScrollPicker용 연도 아이템 (연도별 합계 표시)
    const yearItems = useMemo(() => years.map(year => ({
        value: year,
        label: year.toString(),
        subLabel: formatRevenue(yearlyTotals[year]),
    })), [years, yearlyTotals]);

    // ScrollPicker용 월 아이템 (월별 합계 표시)
    const monthItems = useMemo(() => months.map(month => ({
        value: month,
        label: month.toString(),
        subLabel: formatRevenue(monthlyTotals[month]),
    })), [months, monthlyTotals]);

    // 연도별 합계 로드 (새 API 사용)
    const loadYearlyTotals = useCallback(async () => {
        try {
            const summary = await commissionApi.getYearlySummary();
            const totals = {};
            summary.forEach(item => {
                totals[item.year] = item.total_amount || 0;
            });
            setYearlyTotals(totals);
        } catch (err) {
            console.error('Failed to load yearly summary:', err);
            setYearlyTotals({});
        }
    }, []);

    // 월별 합계 로드 (새 API 사용)
    const loadMonthlyTotals = useCallback(async () => {
        try {
            const summary = await commissionApi.getMonthlySummary(selectedYear);
            const totals = {};
            summary.forEach(item => {
                totals[item.month] = item.total_amount || 0;
            });
            setMonthlyTotals(totals);
        } catch (err) {
            console.error('Failed to load monthly summary:', err);
            setMonthlyTotals({});
        }
    }, [selectedYear]);

    // API에서 데이터 로드
    const loadCommissions = useCallback(async () => {
        setIsLoading(true);
        setError(null);
        try {
            const data = await commissionApi.getCommissions(selectedYear, selectedMonth);
            setCommissions(data.commissions || []);
            setTotalAmount(data.total_amount || 0);
        } catch (err) {
            console.error('Failed to load commissions:', err);
            setError(t('loadError', lang));
            setCommissions([]);
            setTotalAmount(0);
        } finally {
            setIsLoading(false);
        }
    }, [lang, selectedMonth, selectedYear]);

    // 데이터 로드
    useEffect(() => {
        if (hasAccess) {
            loadCommissions();
        }
    }, [hasAccess, loadCommissions]);

    // 연도별 합계 로드 (한 번만)
    useEffect(() => {
        if (hasAccess) {
            loadYearlyTotals();
        }
    }, [hasAccess, loadYearlyTotals]);

    // 월별 합계 로드 (선택된 연도 변경 시)
    useEffect(() => {
        if (hasAccess) {
            loadMonthlyTotals();
        }
    }, [hasAccess, loadMonthlyTotals]);

    // 커미션 저장
    const saveCommission = async () => {
        if (!formData.artist_name || !formData.amount) {
            alert(lang === 'ko' ? '필수 항목을 입력해주세요' : 'Please fill required fields');
            return;
        }

        setIsSaving(true);
        try {
            const data = {
                artist_name: formData.artist_name,
                position: formData.position,
                amount: parseFloat(formData.amount),
                received_date: formData.received_date,
                platform: formData.platform,
                memo: formData.memo || '',
            };

            if (editingCommission) {
                // 수정
                await commissionApi.updateCommission(editingCommission.id, data);
            } else {
                // 추가
                await commissionApi.createCommission(data);
            }

            await loadCommissions();
            closeModal();
        } catch (err) {
            console.error('Failed to save commission:', err);
            alert(t('saveError', lang));
        } finally {
            setIsSaving(false);
        }
    };

    // 커미션 삭제
    const handleDeleteCommission = async (id) => {
        if (!window.confirm(t('confirmDelete', lang))) return;

        try {
            await commissionApi.deleteCommission(id);
            await loadCommissions();
        } catch (err) {
            console.error('Failed to delete commission:', err);
            alert(t('deleteError', lang));
        }
    };

    // 모달 열기 (추가)
    const openAddModal = () => {
        setEditingCommission(null);
        setFormData({
            artist_name: '',
            position: 'resident',
            amount: '',
            received_date: new Date().toISOString().split('T')[0],
            platform: 'cash',
            memo: '',
        });
        setIsModalOpen(true);
    };

    // 모달 열기 (수정)
    const openEditModal = (commission) => {
        setEditingCommission(commission);
        setFormData({
            artist_name: commission.artist_name,
            position: commission.position,
            amount: commission.amount.toString(),
            received_date: commission.received_date,
            platform: commission.platform,
            memo: commission.memo || '',
        });
        setIsModalOpen(true);
    };

    // 모달 닫기
    const closeModal = () => {
        setIsModalOpen(false);
        setEditingCommission(null);
    };

    // 플랫폼 CSV용 표시
    const getPlatformLabelForCSV = (platform) => {
        const platformMap = {
            cash: 'Espèces',
            paypal: 'PayPal',
            transfer: 'Virement',
            other: 'Autre'
        };
        return platformMap[platform] || platform || '-';
    };

    // CSV 미리보기 열기
    const openCsvPreview = async () => {
        let exportCommissions;
        try {
            const result = await commissionApi.getCommissionAccountingExport(selectedYear, selectedMonth);
            exportCommissions = result.commissions || [];
        } catch (error) {
            console.error('Failed to load commission accounting drafts:', error);
            alert('Impossible de charger les données comptables.');
            return;
        }
        if (exportCommissions.length === 0) {
            alert('No data to preview');
            return;
        }

        // 미리보기 데이터 생성
        const data = exportCommissions.map((c) => {
            const receivedDT = DateTime.fromISO(c.received_date);
            const ttc = parseFloat(c.amount) || 0;
            const ht = ttc / 1.20;
            const tva = ttc - ht;

            return {
                id: c.id,
                source_type: 'commission',
                source_id: c.id,
                payment_date: receivedDT.toISODate(),
                payment_date_original: c.received_date_original || receivedDT.toISODate(),
                date: receivedDT.toFormat('dd/MM/yyyy'),
                type: 'Commission',
                ttc: ttc.toFixed(2),
                tva: tva.toFixed(2),
                ht: ht.toFixed(2),
                moyen: getPlatformLabelForCSV(c.platform),
                client: c.artist_name || '-',
                refYear: receivedDT.year,
                refMonth: String(receivedDT.month).padStart(2, '0')
            };
        });

        try {
            setCsvPreviewData(await loadAccountingInvoiceReferences(data));
            setCsvPreviewOpen(true);
        } catch (error) {
            console.error('Failed to load commission invoice references:', error);
            alert('Impossible de charger les numéros de facture.');
        }
    };

    // CSV 다운로드 핸들러
    const handleCsvDownload = (selectedData, selectedRows, allData) => {
        const headers = ['Date', 'Type', 'TTC', 'TVA', 'HT', 'Moyen', 'Client', 'Référence'];

        const csvData = selectedData.map((r) => {
            const reference = getOptionalInvoiceReference(r, '');
            return [r.date, r.type, r.ttc, r.tva, r.ht, r.moyen, r.client, reference];
        });

        const csvContent = [headers, ...csvData]
            .map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(','))
            .join('\n');

        const BOM = '\uFEFF';
        const blob = new Blob([BOM + csvContent], { type: 'text/csv;charset=utf-8;' });
        const filename = `commissions_${selectedYear}_${String(selectedMonth).padStart(2, '0')}.csv`;

        const link = document.createElement('a');
        if (link.download !== undefined) {
            const url = URL.createObjectURL(blob);
            link.setAttribute('href', url);
            link.setAttribute('download', filename);
            link.style.visibility = 'hidden';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        }
    };

    // Facture PDF 다운로드 핸들러 (특수 권한 사용자 전용)
    const handleFactureDownload = (selectedData) => {
        if (!canExportFacture) return;
        const issuedData = selectedData.filter(row => row.invoice_reference);
        if (issuedData.length === 0) {
            alert('Aucune facture émise depuis les crédits rapprochés.');
            return;
        }
        downloadInvoicePdf(issuedData, { type: 'commission', year: selectedYear, month: selectedMonth });
    };

    // 접근 권한 없음
    if (!hasAccess) {
        return (
            <div className={styles.noAccess}>
                {t('noAccess', lang)}
            </div>
        );
    }

    return (
        <div className={styles.pageContainer}>
            {/* 헤더 */}
            <h1 className={styles.pageTitle} style={{ color: currentTheme?.activeColor }}>
                {t('title', lang)}
            </h1>

            {/* 년/월 스크롤 피커 */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', marginBottom: '12px' }}>
                <ScrollPicker
                    items={yearItems}
                    selectedValue={selectedYear}
                    onChange={setSelectedYear}
                />
                <ScrollPicker
                    items={monthItems}
                    selectedValue={selectedMonth}
                    onChange={setSelectedMonth}
                    isMonth={true}
                />
            </div>

            {/* 총 커미션 & 추가 버튼 */}
            <div className={styles.summaryRow}>
                <div className={styles.totalAmount}>
                    <span className={styles.totalLabel}>{t('totalCommission', lang)}</span>
                    <span className={styles.totalValue}>€{totalAmount.toFixed(2)}</span>
                </div>
                <div style={{ display: 'flex', gap: '8px' }}>
                    <button
                        onClick={openCsvPreview}
                        style={{
                            padding: '6px 12px',
                            backgroundColor: '#2e7d32',
                            color: '#ffffff',
                            border: 'none',
                            borderRadius: '6px',
                            fontSize: '12px',
                            fontWeight: '500',
                            cursor: 'pointer',
                            textTransform: 'uppercase'
                        }}
                    >
                        CSV
                    </button>
                    <button
                        className={styles.addButton}
                        onClick={openAddModal}
                    >
                        + {t('add', lang)}
                    </button>
                </div>
            </div>

            {/* 에러 표시 */}
            {error && (
                <div className={styles.error}>{error}</div>
            )}

            {/* 커미션 리스트 - 카드 스타일 */}
            {isLoading ? (
                <div className={styles.loading}>Loading...</div>
            ) : commissions.length === 0 ? (
                <div className={styles.noData}>{t('noData', lang)}</div>
            ) : (
                <div className={styles.listContainer}>
                    {commissions.map(commission => (
                        <div
                            key={commission.id}
                            className={styles.commissionCard}
                            onClick={() => openEditModal(commission)}
                        >
                            <div className={styles.cardLeft}>
                                <div className={styles.cardName}>{commission.artist_name}</div>
                                <div className={styles.cardMeta}>
                                    <span>{(commission.received_date || '').split('T')[0]}</span>
                                    <span>{t(commission.position, lang)}</span>
                                    <span>{t(commission.platform, lang)}</span>
                                </div>
                            </div>
                            <span className={styles.cardAmount}>€{Number(commission.amount || 0).toFixed(2)}</span>
                            <button
                                className={styles.deleteBtn}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handleDeleteCommission(commission.id);
                                }}
                            >
                                ×
                            </button>
                        </div>
                    ))}
                </div>
            )}

            {/* 추가/수정 모달 */}
            {isModalOpen && (
                <div className={commonStyles.modalOverlay} onClick={closeModal}>
                    <div className={commonStyles.modal} onClick={e => e.stopPropagation()}>
                        <div className={commonStyles.modalHeader}>
                            <h3>{editingCommission ? t('editCommission', lang) : t('addCommission', lang)}</h3>
                            <button className={commonStyles.closeButton} onClick={closeModal}>×</button>
                        </div>
                        <div className={styles.modalBody}>
                            {/* 타투이스트 이름 */}
                            <div className={styles.formGroup}>
                                <label>{t('artistName', lang)} *</label>
                                <input
                                    type="text"
                                    value={formData.artist_name}
                                    onChange={(e) => setFormData({ ...formData, artist_name: e.target.value })}
                                    className={styles.input}
                                />
                            </div>

                            {/* 지위 */}
                            <div className={styles.formGroup}>
                                <label>{t('position', lang)} *</label>
                                <select
                                    value={formData.position}
                                    onChange={(e) => setFormData({ ...formData, position: e.target.value })}
                                    className={styles.select}
                                >
                                    {POSITION_OPTIONS.map(pos => (
                                        <option key={pos} value={pos}>{t(pos, lang)}</option>
                                    ))}
                                </select>
                            </div>

                            {/* 금액 */}
                            <div className={styles.formGroup}>
                                <label>{t('amount', lang)} (€) *</label>
                                <input
                                    type="number"
                                    step="0.01"
                                    min="0"
                                    value={formData.amount}
                                    onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                                    className={styles.input}
                                />
                            </div>

                            {/* 받은 날짜 */}
                            <div className={styles.formGroup}>
                                <label>{t('receivedDate', lang)} *</label>
                                <input
                                    type="date"
                                    value={formData.received_date}
                                    onChange={(e) => setFormData({ ...formData, received_date: e.target.value })}
                                    className={styles.input}
                                />
                            </div>

                            {/* 플랫폼 */}
                            <div className={styles.formGroup}>
                                <label>{t('platform', lang)} *</label>
                                <select
                                    value={formData.platform}
                                    onChange={(e) => setFormData({ ...formData, platform: e.target.value })}
                                    className={styles.select}
                                >
                                    {PLATFORM_OPTIONS.map(plat => (
                                        <option key={plat} value={plat}>{t(plat, lang)}</option>
                                    ))}
                                </select>
                            </div>

                            {/* 메모 */}
                            <div className={styles.formGroup}>
                                <label>{t('memo', lang)}</label>
                                <textarea
                                    value={formData.memo}
                                    onChange={(e) => setFormData({ ...formData, memo: e.target.value })}
                                    className={styles.textarea}
                                    rows={3}
                                />
                            </div>

                            {/* 버튼 */}
                            <div className={styles.modalButtons}>
                                <button
                                    className={styles.cancelBtn}
                                    onClick={closeModal}
                                    disabled={isSaving}
                                >
                                    {t('cancel', lang)}
                                </button>
                                <button
                                    className={styles.saveBtn}
                                    onClick={saveCommission}
                                    disabled={isSaving}
                                >
                                    {isSaving ? '...' : t('save', lang)}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* CSV 미리보기 모달 */}
            <CsvPreviewModal
                isOpen={csvPreviewOpen}
                onClose={() => setCsvPreviewOpen(false)}
                data={csvPreviewData}
                onDownloadCsv={handleCsvDownload}
                onDownloadFacture={handleFactureDownload}
                showFactureButton={canExportFacture}
                lang={lang}
                refPrefix="C"
            />
        </div>
    );
}
